<template>
  <v-container fluid class="pa-0">
    <v-row>
      <v-col cols="12">
        <v-card>
          <v-card-title class="d-flex align-center pb-0">
            <v-icon left>{{ icons.mdiTextBoxMultiple }}</v-icon>
            {{ title }}
            <v-spacer></v-spacer>
            <v-btn color="primary" @click="refreshAll" :loading="loading">
              <v-icon left>{{ icons.mdiRefresh }}</v-icon>
              Actualiser
            </v-btn>
          </v-card-title>

          <v-tabs v-model="activeTab" class="px-4">
            <v-tab>
              <v-icon left small>{{ icons.mdiFileDocumentEdit }}</v-icon>
              Templates de Prompts
            </v-tab>
            <v-tab>
              <v-icon left small>{{ icons.mdiLinkVariant }}</v-icon>
              Assignations (Bindings)
            </v-tab>
            <v-tab>
              <v-icon left small>{{ icons.mdiTimelineText }}</v-icon>
              Logs d'exécution
            </v-tab>
          </v-tabs>

          <v-tabs-items v-model="activeTab">
            <v-tab-item>
              <v-card-text class="pt-6">
                <div class="d-flex align-center mb-4">
                  <div class="text-subtitle-1">Templates disponibles</div>
                  <v-spacer></v-spacer>
                  <v-text-field
                    v-model="templateSearch"
                    dense
                    outlined
                    clearable
                    hide-details
                    class="mr-3 prompt-search-field"
                    label="Rechercher par nom"
                    :prepend-inner-icon="icons.mdiMagnify"
                  ></v-text-field>
                  <v-btn color="success" small @click="openNewTemplate">
                    <v-icon left small>{{ icons.mdiPlus }}</v-icon>
                    Nouveau Template
                  </v-btn>
                </div>

                <v-data-table
                  :headers="headers"
                  :items="searchedTemplates"
                  :loading="loading"
                  class="elevation-0"
                >
                  <template #[`item.name`]="{ item }">
                    <div class="font-weight-bold">{{ item.name }}</div>
                    <div class="text-caption grey--text">
                      {{ item.description }}
                    </div>
                  </template>

                  <template #[`item.logging_enabled`]="{ item }">
                    <v-switch
                      v-model="item.logging_enabled"
                      dense
                      hide-details
                      class="mt-0 pt-0"
                      color="primary"
                      @change="toggleLogging(item)"
                    ></v-switch>
                  </template>

                  <template #[`item.last_used`]="{ item }">
                    {{
                      item.last_used
                        ? new Date(item.last_used).toLocaleString()
                        : "Jamais"
                    }}
                  </template>

                  <template #[`item.actions`]="{ item }">
                    <div class="d-flex align-center justify-end">
                      <v-tooltip top>
                        <template v-slot:activator="{ on, attrs }">
                          <v-btn
                            icon
                            small
                            v-bind="attrs"
                            v-on="on"
                            color="primary"
                            @click="previewActiveRevision(item)"
                            class="mr-1"
                          >
                            <v-icon small>{{ icons.mdiEye }}</v-icon>
                          </v-btn>
                        </template>
                        <span>Voir la version active</span>
                      </v-tooltip>

                      <v-tooltip top>
                        <template v-slot:activator="{ on, attrs }">
                          <v-btn
                            icon
                            small
                            v-bind="attrs"
                            v-on="on"
                            color="secondary"
                            @click="openTemplateMetaEditor(item)"
                            class="mr-1"
                          >
                            <v-icon small>{{ icons.mdiPencil }}</v-icon>
                          </v-btn>
                        </template>
                        <span>Modifier le nom et la description</span>
                      </v-tooltip>

                      <v-tooltip top>
                        <template v-slot:activator="{ on, attrs }">
                          <v-btn
                            icon
                            small
                            v-bind="attrs"
                            v-on="on"
                            :color="isTemplateAssigned(item.id) ? 'success' : 'error'"
                            @click="assignTemplate(item)"
                            class="mr-1"
                          >
                            <v-icon small>{{ icons.mdiLinkPlus }}</v-icon>
                          </v-btn>
                        </template>
                        <span>{{
                          isTemplateAssigned(item.id)
                            ? "Assignation déjà configurée"
                            : "Template non-assigné !"
                        }}</span>
                      </v-tooltip>

                      <v-btn
                        small
                        color="info"
                        @click="openRevisionManager(item)"
                        class="mr-2"
                      >
                        <v-icon left small>{{ icons.mdiHistory }}</v-icon>
                        Versions
                      </v-btn>

                      <v-btn
                        v-if="isDevMode"
                        icon
                        small
                        color="error"
                        @click="confirmDeleteTemplate(item)"
                      >
                        <v-icon small>{{ icons.mdiTrashCan }}</v-icon>
                      </v-btn>
                    </div>
                  </template>
                </v-data-table>
              </v-card-text>
            </v-tab-item>

            <v-tab-item>
              <v-card-text class="pt-6">
                <div class="d-flex align-center mb-4">
                  <div class="text-subtitle-1">Assignations actives</div>
                  <v-spacer></v-spacer>
                  <v-text-field
                    v-model="bindingSearch"
                    dense
                    outlined
                    clearable
                    hide-details
                    class="mr-3 prompt-search-field"
                    label="Rechercher par nom"
                    :prepend-inner-icon="icons.mdiMagnify"
                  ></v-text-field>
                  <v-btn color="success" small @click="openNewBinding">
                    <v-icon left small>{{ icons.mdiPlus }}</v-icon>
                    Nouvelle Assignation
                  </v-btn>
                </div>

                <v-data-table
                  :headers="bindingHeaders"
                  :items="searchedBindings"
                  :loading="loading"
                  class="elevation-0"
                  :sort-by="['context_code', 'tool_step']"
                  :sort-desc="[false, false]"
                  multi-sort
                >
                  <template #[`item.context_code`]="{ item }">
                    <span>{{ item.context_code }}</span>
                  </template>

                  <template #[`item.tool_step`]="{ item }">
                    <v-chip v-if="item.tool_step" x-small label outlined>{{
                      item.tool_step
                    }}</v-chip>
                  </template>

                  <template #[`item.site_id`]="{ item }">
                    <span v-if="item.site_id" class="font-weight-medium">
                      Site: {{ getSiteName(item.site_id) }}
                      <span class="text-caption grey--text"
                        >(ID: {{ item.site_id }})</span
                      >
                    </span>
                    <v-chip v-else x-small color="primary" dark label
                      >GLOBAL DEFAULT</v-chip
                    >
                  </template>

                  <template #[`item.template_id`]="{ item }">
                    {{ getTemplateName(item.template_id) }}
                  </template>

                  <template #[`item.model_id`]="{ item }">
                    <v-chip small color="primary" outlined>{{
                      item.model_id
                    }}</v-chip>
                  </template>

                  <template #[`item.api_key_env_vars`]="{ item }">
                    <span v-if="item.api_key_env_vars && item.api_key_env_vars.length === 1">
                      {{ item.api_key_env_vars[0] }}
                    </span>
                    <v-chip v-else-if="item.api_key_env_vars && item.api_key_env_vars.length > 1" small outlined>
                      {{ item.api_key_env_vars.length }} keys
                    </v-chip>
                    <v-chip v-else x-small color="error" dark label>AUCUNE CLÉ</v-chip>
                  </template>

                  <template #[`item.is_active`]="{ item }">
                    <v-icon small :color="item.is_active ? 'success' : 'grey'">
                      {{
                        item.is_active
                          ? icons.mdiCheckCircle
                          : icons.mdiCloseCircle
                      }}
                    </v-icon>
                  </template>

                  <template #[`item.actions`]="{ item }">
                    <div class="d-flex">
                      <!-- Restreint par site : un binding global se lit et se
                           duplique, mais ne s'edite ni ne se supprime. -->
                      <v-btn
                        v-if="canWriteBinding(item)"
                        icon
                        small
                        @click="openEditBinding(item)"
                        color="primary"
                      >
                        <v-icon small>{{ icons.mdiPencil }}</v-icon>
                      </v-btn>
                      <v-btn
                        icon
                        small
                        @click="duplicateBinding(item)"
                        color="secondary"
                      >
                        <v-icon small>{{ icons.mdiContentDuplicate }}</v-icon>
                      </v-btn>
                      <v-btn
                        v-if="canWriteBinding(item)"
                        icon
                        small
                        @click="confirmDeleteBinding(item)"
                        color="error"
                      >
                        <v-icon small>{{ icons.mdiTrashCan }}</v-icon>
                      </v-btn>
                    </div>
                  </template>
                </v-data-table>
              </v-card-text>
            </v-tab-item>

            <v-tab-item>
               <prompt-logs ref="logsTab" />
            </v-tab-item>
          </v-tabs-items>
        </v-card>
      </v-col>
    </v-row>

    <v-dialog v-model="revisionDialog" max-width="1200px">
      <v-card v-if="selectedTemplate">
        <v-card-title>
          Révisions : {{ selectedTemplate.name }}
          <v-spacer></v-spacer>
          <v-tooltip top v-if="isDevMode">
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                icon
                small
                class="mr-2"
                color="secondary"
                v-bind="attrs"
                v-on="on"
                @click="openTemplateMetaEditor(selectedTemplate)"
              >
                <v-icon small>{{ icons.mdiPencil }}</v-icon>
              </v-btn>
            </template>
            <span>[DEV] Modifier le nom et la description</span>
          </v-tooltip>
          <v-btn color="primary" @click="openPromptEditor()">
            <v-icon left>{{ icons.mdiPlus }}</v-icon>
            Nouvelle Version
          </v-btn>
        </v-card-title>

        <v-data-table
          :headers="revisionHeaders"
          :items="revisions"
          :loading="loadingRevisions"
          class="elevation-0"
        >
          <template #[`item.version_number`]="{ item }">
            <v-chip small color="primary" outlined
              >v{{ item.version_number }}</v-chip
            >
            <v-chip
              v-if="item.id === selectedTemplate.current_revision_id"
              x-small
              color="success"
              class="ml-2"
              >Active</v-chip
            >
          </template>

          <template #[`item.prompts`]="{ item }">
            <div class="text-truncate" style="max-width: 300px">
              <strong>System:</strong> {{ item.system_prompt }}
            </div>
            <div class="text-truncate" style="max-width: 300px">
              <strong>User:</strong> {{ item.user_prompt }}
            </div>
          </template>

          <template #[`item.actions`]="{ item }">
            <div class="d-flex">
              <v-tooltip top>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    icon
                    small
                    v-bind="attrs"
                    v-on="on"
                    @click="openPreview(item)"
                  >
                    <v-icon small color="primary">{{ icons.mdiEye }}</v-icon>
                  </v-btn>
                </template>
                <span>Aperçu complet</span>
              </v-tooltip>

              <v-tooltip
                top
                v-if="isDevMode && item.id === selectedTemplate.current_revision_id"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    icon
                    small
                    v-bind="attrs"
                    v-on="on"
                    @click="openDirectEditor(item)"
                    color="orange"
                  >
                    <v-icon small>{{ icons.mdiPencil }}</v-icon>
                  </v-btn>
                </template>
                <span>[DEV] Modifier cette version</span>
              </v-tooltip>

              <v-tooltip top>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    icon
                    small
                    v-bind="attrs"
                    v-on="on"
                    @click="cloneRevision(item)"
                    color="secondary"
                  >
                    <v-icon small>{{ icons.mdiContentDuplicate }}</v-icon>
                  </v-btn>
                </template>
                <span>Cloner pour éditer</span>
              </v-tooltip>

              <v-tooltip
                top
                v-if="item.id !== selectedTemplate.current_revision_id"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    icon
                    small
                    v-bind="attrs"
                    v-on="on"
                    @click="activateRevision(item)"
                    color="success"
                  >
                    <v-icon small>{{ icons.mdiPlayCircle }}</v-icon>
                  </v-btn>
                </template>
                <span>Rendre cette version ACTIVE</span>
              </v-tooltip>
            </div>
          </template>
        </v-data-table>
      </v-card>
    </v-dialog>

    <v-dialog v-model="templateDialog" max-width="800px">
      <v-card>
        <v-card-title>Nouveau Template de Prompt</v-card-title>
        <v-card-text>
          <v-text-field
            v-model="newTemplate.name"
            label="Nom du Template"
            outlined
            class="mt-4"
            :hint="'Utilisez un préfixe pertinent (ex: ' + defaultNewTemplatePrefix + ')'"
            persistent-hint
          ></v-text-field>

          <v-textarea
            v-model="newTemplate.description"
            label="Description"
            outlined
            rows="2"
            class="mt-4"
          ></v-textarea>

          <v-divider class="my-4"></v-divider>
          <div class="text-subtitle-2 mb-2">Version Initiale (v1)</div>

          <v-textarea
            v-model="newTemplate.initial_revision.system_prompt"
            label="System Prompt initial"
            outlined
            rows="3"
          ></v-textarea>

          <v-textarea
            v-model="newTemplate.initial_revision.user_prompt"
            label="User Prompt initial"
            outlined
            rows="5"
          ></v-textarea>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="templateDialog = false">Annuler</v-btn>
          <v-btn
            color="success"
            @click="saveTemplate"
            :loading="saving"
            :disabled="!newTemplate.name"
            >Créer le Template</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="templateMetaDialog" max-width="700px">
      <v-card>
        <v-card-title>Modifier le template</v-card-title>
        <v-card-text>
          <v-text-field
            v-model="templateMetaForm.name"
            label="Nom du Template"
            outlined
            class="mt-4"
          ></v-text-field>

          <v-textarea
            v-model="templateMetaForm.description"
            label="Description"
            outlined
            rows="3"
            class="mt-4"
          ></v-textarea>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="templateMetaDialog = false">Annuler</v-btn>
          <v-btn
            color="primary"
            @click="saveTemplateMeta"
            :loading="saving"
            :disabled="!templateMetaForm.name"
          >
            Sauvegarder
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="previewDialog" max-width="900px">
      <v-card v-if="previewItem">
        <v-card-title class="d-flex align-center grey lighten-4">
          Aperçu Version v{{ previewItem.version_number }}
          <v-spacer></v-spacer>
          <v-chip
            small
            color="primary"
            outlined
            v-if="selectedTemplate && previewItem.id === selectedTemplate.current_revision_id"
            >ACTIVE</v-chip
          >
        </v-card-title>
        <v-card-text class="pt-4">
          <div class="text-overline mb-1 grey--text text--darken-1">
            System Prompt
          </div>
          <v-sheet class="code-block mb-4">
            {{ previewItem.system_prompt }}
          </v-sheet>

          <div class="text-overline mb-1 grey--text text--darken-1">
            User Prompt
          </div>
          <v-sheet class="code-block">
            {{ previewItem.user_prompt }}
          </v-sheet>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" text @click="previewDialog = false">Fermer</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="editorDialog" max-width="1000px" persistent>
      <v-card v-if="editRevision">
        <v-card-title>
          {{
            editRevision.mode === "edit"
              ? "[DEV] Modification directe v" + editRevision.version_number
              : "Nouvelle version"
          }}
          pour {{ selectedTemplate ? selectedTemplate.name : "" }}
        </v-card-title>
        <v-card-text>
          <template v-if="isDevMode && editRevision.mode === 'edit'">
            <v-text-field
              v-model="editorTemplateMeta.name"
              label="Nom du Template"
              outlined
              class="mt-4"
            ></v-text-field>

            <v-textarea
              v-model="editorTemplateMeta.description"
              label="Description du Template"
              outlined
              rows="2"
              class="mt-4"
            ></v-textarea>
          </template>

          <v-textarea
            v-model="editRevision.system_prompt"
            label="System Prompt"
            outlined
            rows="5"
            hint="Instructions globales pour l'IA"
            persistent-hint
            class="mt-4"
          ></v-textarea>

          <v-textarea
            v-model="editRevision.user_prompt"
            label="User Prompt"
            outlined
            rows="10"
            :hint="'Utilisez {{ variable }} pour les variables Jinja2'"
            persistent-hint
            class="mt-4"
          ></v-textarea>

          <v-text-field
            v-model="editRevision.commit_message"
            label="Message de changement"
            placeholder="Ex: Amélioration du ton journalistique"
            outlined
            class="mt-4"
          ></v-text-field>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="editorDialog = false">Annuler</v-btn>
          <v-btn color="primary" @click="saveRevision" :loading="saving">Sauvegarder</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="bindingDialog" max-width="900px">
      <v-card>
        <v-card-title>{{ isEditingBinding ? "Modifier l'assignation" : "Nouvelle Assignation" }}</v-card-title>
        <v-card-text>
          <v-row class="mt-2">
            <v-col cols="6">
              <v-select
                v-model="newBinding.context_code"
                :items="filteredContexts"
                item-text="label"
                item-value="code"
                label="Contexte"
                outlined
                dense
              ></v-select>
            </v-col>
            <v-col cols="6">
              <v-select
                v-if="dynamicToolSteps && dynamicToolSteps.length"
                v-model="newBinding.tool_step"
                :items="dynamicToolSteps"
                item-text="label"
                item-value="step_key"
                label="Étape / Format (Optionnel)"
                outlined
                dense
                clearable
              ></v-select>
              <div v-else class="text-caption grey--text pt-2">
                <em>(Aucune étape / sous-tâche configurée pour ce contexte)</em>
              </div>
            </v-col>
            <v-col cols="6">
              <v-autocomplete
                v-model="newBinding.site_id"
                :items="selectableSites"
                item-text="site.name"
                item-value="site.id"
                :label="
                  isSiteRestricted
                    ? 'Site'
                    : 'Site Spécifique (Laisser vide pour Défaut Global)'
                "
                outlined
                dense
                :clearable="!isSiteRestricted"
              ></v-autocomplete>
            </v-col>
            <v-col cols="6">
              <v-select
                v-model="newBinding.template_id"
                :items="filteredTemplates"
                item-text="name"
                item-value="id"
                label="Template à utiliser"
                outlined
                dense
              ></v-select>
            </v-col>
            <v-col cols="12">
              <v-select
                v-model="newBinding.model_id"
                :items="selectableModels"
                item-text="id"
                item-value="id"
                label="Modèle IA"
                outlined
                dense
              ></v-select>
            </v-col>
            <v-col cols="12">
              <v-combobox
                v-model="newBinding.api_key_env_vars"
                :items="envKeyOptions"
                label="Variables d'environnement API Key"
                outlined
                dense
                multiple
                chips
                small-chips
                deletable-chips
                hint="Une clé minimum. Si plusieurs sont configurées, le backend en choisira une aléatoirement."
                persistent-hint
              ></v-combobox>
            </v-col>
          </v-row>

          <v-textarea
            v-model="bindingConfigJson"
            label="Configuration d'exécution (JSON)"
            outlined
            rows="4"
            placeholder='{ "temperature": 0.7 }'
            hint="Surcharge les paramètres par défaut du modèle"
            persistent-hint
          ></v-textarea>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="bindingDialog = false">Annuler</v-btn>
          <v-btn color="success" @click="saveBinding" :loading="saving">Sauvegarder l'assignation</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { ref, onMounted, onUnmounted, computed, watch } from "@vue/composition-api";
import {
  mdiTextBoxMultiple, mdiRefresh, mdiHistory, mdiLink, mdiLinkVariant,
  mdiPlus, mdiEye, mdiFileDocumentEdit, mdiCheckCircle, mdiCloseCircle,
  mdiMagnify, mdiTrashCan, mdiPencil, mdiContentDuplicate, mdiPlayCircle,
  mdiLinkPlus, mdiTimelineText
} from "@mdi/js";
import { Konami, KonamiManager } from "@/utils/konami";
import axios from "@axios";
import llprService from "@/services/llpr.service";
import store from "@/store";
import PromptLogs from './PromptLogs.vue'

export default {
  name: "PromptManager",
  components: {
    PromptLogs
  },
  props: {
    title: { type: String, default: "Configuration des Prompts" },
    prefixes: { type: Array, default: () => [] },
    defaultNewTemplatePrefix: { type: String, default: "" },
    // Si non vide, restreint le selecteur de modele aux providers listes
    // (ex: ['perplexity'] pour la recherche d'URLs via_angles).
    allowedProviders: { type: Array, default: () => [] },
  },
  setup(props) {
    const activeTab = ref(0);
    const loading = ref(false);
    const templates = ref([]);
    const bindings = ref([]);
    const contexts = ref([]);
    const availableModels = ref([]);
    const allSites = ref([]);
    const envKeyOptions = ref([]);
    const templateSearch = ref("");
    const bindingSearch = ref("");

    const selectedTemplate = ref(null);
    const revisionDialog = ref(false);
    const loadingRevisions = ref(false);
    const revisions = ref([]);
    const previewDialog = ref(false);
    const previewItem = ref(null);
    const editorDialog = ref(false);
    const saving = ref(false);
    const templateDialog = ref(false);
    const templateMetaDialog = ref(false);
    const bindingDialog = ref(false);
    const isEditingBinding = ref(false);
    const bindingConfigJson = ref("{}");

    const editRevision = ref({
      id: null,
      system_prompt: "",
      user_prompt: "",
      commit_message: "",
      mode: "new",
      version_number: null,
    });

    const newTemplate = ref({
      name: "",
      description: "",
      initial_revision: {
        system_prompt: "",
        user_prompt: "",
        commit_message: "Version initiale",
      },
    });

    const templateMetaForm = ref({
      id: null,
      name: "",
      description: "",
      is_shared: false,
      is_active: true,
      logging_enabled: false,
    });

    const editorTemplateMeta = ref({
      name: "",
      description: "",
    });

    const newBinding = ref({
      context_code: "",
      tool_step: null,
      site_id: null,
      template_id: null,
      model_id: "",
      api_key_env_vars: [],
      execution_config: {},
      is_default: false,
      is_active: true,
    });

    const isDevMode = ref(false);
    let konamiManager = null;

    // Acces par site : le back fait foi, le front n'offre que ce qui passera.
    const isSiteRestricted = computed(() => store.getters["common/getIsSiteRestricted"]);
    const selectableSites = computed(() => {
      if (!isSiteRestricted.value) return allSites.value;
      const accessible = store.getters["common/getAccessibleSiteIds"] || [];
      return allSites.value.filter((s) => accessible.includes(s.site?.id));
    });
    const canWriteBinding = (binding) => !isSiteRestricted.value || !!binding.site_id;

    // --- Computed Filters (Your Feature) ---
    const filteredTemplates = computed(() => {
      if (!props.prefixes.length) return templates.value;
      const matchingTemplateIds = new Set(
        bindings.value
          .filter((b) => props.prefixes.some((p) => b.context_code.startsWith(p)))
          .map((b) => b.template_id)
      );
      return templates.value.filter(
        (t) =>
          props.prefixes.some((p) => t.name.startsWith(p)) ||
          matchingTemplateIds.has(t.id)
      );
    });

    const searchedTemplates = computed(() => {
      const search = templateSearch.value?.trim().toLowerCase();
      if (!search) return filteredTemplates.value;
      return filteredTemplates.value.filter((template) => {
        const haystack = [template.name, template.description].filter(Boolean).join(" ").toLowerCase();
        return haystack.includes(search);
      });
    });

    const filteredBindings = computed(() => {
      if (!props.prefixes.length) return bindings.value;
      return bindings.value.filter((b) =>
        props.prefixes.some((p) => b.context_code.startsWith(p))
      );
    });

    const searchedBindings = computed(() => {
      const search = bindingSearch.value?.trim().toLowerCase();
      if (!search) return filteredBindings.value;
      return filteredBindings.value.filter((binding) => {
        const templateName = templates.value.find(t => t.id === binding.template_id)?.name;
        const siteName = allSites.value.find(s => s.site?.id === binding.site_id)?.site?.name;
        const apiKeys = Array.isArray(binding.api_key_env_vars) ? binding.api_key_env_vars.join(" ") : "";
        const haystack = [binding.context_code, binding.tool_step, templateName, siteName, binding.model_id, apiKeys].filter(Boolean).join(" ").toLowerCase();
        return haystack.includes(search);
      });
    });

    const filteredContexts = computed(() => {
      if (!props.prefixes.length) return contexts.value;
      return contexts.value.filter((c) => props.prefixes.some((p) => c.code.startsWith(p)));
    });

    const dynamicToolSteps = computed(() => {
      const ctx = contexts.value.find((c) => c.code === newBinding.value.context_code);
      return ctx ? ctx.steps : [];
    });

    const selectedModel = computed(() => availableModels.value.find((m) => m.id === newBinding.value.model_id));
    const selectedProviderId = computed(() => selectedModel.value?.provider_id || null);
    const selectableModels = computed(() =>
      props.allowedProviders.length
        ? availableModels.value.filter((m) => props.allowedProviders.includes(m.provider_id))
        : availableModels.value
    );

    // --- Table Headers (Merged) ---
    const headers = [
      { text: "Template", value: "name" },
      { text: "Logging", value: "logging_enabled" }, // From Dev
      { text: "Utilisé", value: "times_used" },
      { text: "Dernier usage", value: "last_used" },
      { text: "Actions", value: "actions", sortable: false, align: "end" },
    ];

    const bindingHeaders = [
      { text: "Contexte", value: "context_code", sortable: true },
      { text: "Étape", value: "tool_step", sortable: true },
      { text: "Portée (Site)", value: "site_id" },
      { text: "Template", value: "template_id" },
      { text: "Modèle", value: "model_id" },
      { text: "API Keys", value: "api_key_env_vars", sortable: false },
      { text: "Actif", value: "is_active", align: "center" },
      { text: "Actions", value: "actions", sortable: false, align: "end" },
    ];

    const revisionHeaders = [
      { text: "Version", value: "version_number" },
      { text: "Contenu", value: "prompts", sortable: false },
      { text: "Message", value: "commit_message" },
      { text: "Date", value: "created" },
      { text: "Actions", value: "actions", sortable: false },
    ];

    // --- Methods ---
    const refreshAll = async () => {
      loading.value = true;
      try {
        const [tRes, bRes, cRes, mRes, sRes] = await Promise.all([
          llprService.getTemplates(),
          llprService.getBindings(),
          llprService.getContexts(),
          llprService.getModels(),
          axios.get("/api/connectors/", { params: { api_allowed: true, limit: 0 } }),
        ]);
        templates.value = tRes.data;
        bindings.value = bRes.data;
        contexts.value = cRes.data;
        availableModels.value = mRes.data;
        allSites.value = sRes.data.items || [];
      } catch (error) {
        console.error("Failed to refresh data:", error);
      } finally {
        loading.value = false;
      }
    };

    const loadEnvKeyOptions = async () => {
      if (!selectedProviderId.value) {
        envKeyOptions.value = [];
        newBinding.value.api_key_env_vars = [];
        return;
      }
      try {
        const res = await llprService.getEnvKeys(selectedProviderId.value);
        envKeyOptions.value = res.data || [];
        const allowed = new Set(envKeyOptions.value);
        newBinding.value.api_key_env_vars = (newBinding.value.api_key_env_vars || []).filter(key => allowed.has(key));
      } catch (error) {
        envKeyOptions.value = [];
        console.error("Failed to load env keys:", error);
      }
    };

    const toggleLogging = async (item) => {
      try {
        await llprService.toggleTemplateLogging(item.id, item.logging_enabled)
        store.dispatch('app/setSnackBar', { 
          show: true, 
          text: `Logging ${item.logging_enabled ? 'activé' : 'désactivé'} pour ${item.name}`, 
          color: 'info' 
        })
      } catch (error) {
        item.logging_enabled = !item.logging_enabled 
        store.dispatch('app/setSnackBar', { show: true, text: 'Erreur lors du changement de logging', color: 'error' })
      }
    };

    // [Note: Autres méthodes saveRevision, openRevisionManager, etc. restent identiques]
    const openRevisionManager = async (template) => {
      selectedTemplate.value = template;
      revisionDialog.value = true;
      loadingRevisions.value = true;
      try {
        const res = await llprService.getRevisions(template.id);
        revisions.value = res.data;
      } catch (error) {
        console.error("Failed to fetch revisions:", error);
      } finally {
        loadingRevisions.value = false;
      }
    };

    const openPromptEditor = (initialData = null) => {
      editorTemplateMeta.value = {
        name: selectedTemplate.value?.name || "",
        description: selectedTemplate.value?.description || "",
      };

      if (initialData) {
        editRevision.value = {
          mode: "new",
          system_prompt: initialData.system_prompt || "",
          user_prompt: initialData.user_prompt || "",
          commit_message: `Reprise de la version v${initialData.version_number}`,
        };
      } else {
        const latest = revisions.value[0] || {};
        editRevision.value = {
          mode: "new",
          system_prompt: latest.system_prompt || "",
          user_prompt: latest.user_prompt || "",
          commit_message: "",
        };
      }
      editorDialog.value = true;
    };

    const saveRevision = async () => {
      if (!editRevision.value.commit_message) {
        alert("Saisir un message de commit");
        return;
      }

      if (isDevMode.value && editRevision.value.mode === "edit" && !editorTemplateMeta.value.name?.trim()) {
        store.dispatch("app/setSnackBar", {
          show: true,
          text: "Le nom du template est obligatoire",
          color: "error",
        });
        return;
      }

      saving.value = true;
      try {
        if (editRevision.value.mode === "edit") {
          await llprService.updateRevision(editRevision.value.id, editRevision.value);

          const nextName = editorTemplateMeta.value.name?.trim();
          const nextDescription = editorTemplateMeta.value.description?.trim() || null;
          const currentDescription = selectedTemplate.value?.description || null;

          if (
            selectedTemplate.value &&
            (nextName !== selectedTemplate.value.name || nextDescription !== currentDescription)
          ) {
            const templateRes = await llprService.updateTemplate(selectedTemplate.value.id, {
              name: nextName,
              description: nextDescription,
              is_shared: !!selectedTemplate.value.is_shared,
              is_active: selectedTemplate.value.is_active !== false,
              logging_enabled: !!selectedTemplate.value.logging_enabled,
            });
            syncTemplateState(templateRes.data);
          }
        } else {
          await llprService.createRevision(selectedTemplate.value.id, editRevision.value);
        }
        editorDialog.value = false;
        await openRevisionManager(selectedTemplate.value);
        await refreshAll();
      } finally {
        saving.value = false;
      }
    };

    const openNewTemplate = () => {
      newTemplate.value = {
        name: props.defaultNewTemplatePrefix,
        description: "",
        initial_revision: { system_prompt: "", user_prompt: "", commit_message: "Version initiale" },
      };
      templateDialog.value = true;
    };

    const saveTemplate = async () => {
      saving.value = true;
      try {
        await llprService.createTemplate(newTemplate.value);
        templateDialog.value = false;
        await refreshAll();
      } finally {
        saving.value = false;
      }
    };

    const syncTemplateState = (updatedTemplate) => {
      templates.value = templates.value.map((template) =>
        template.id === updatedTemplate.id ? { ...template, ...updatedTemplate } : template
      );

      if (selectedTemplate.value?.id === updatedTemplate.id) {
        selectedTemplate.value = { ...selectedTemplate.value, ...updatedTemplate };
      }
    };

    const openTemplateMetaEditor = (template) => {
      if (!template) return;

      templateMetaForm.value = {
        id: template.id,
        name: template.name || "",
        description: template.description || "",
        is_shared: !!template.is_shared,
        is_active: template.is_active !== false,
        logging_enabled: !!template.logging_enabled,
      };
      templateMetaDialog.value = true;
    };

    const saveTemplateMeta = async () => {
      const name = templateMetaForm.value.name?.trim();
      if (!name) {
        store.dispatch("app/setSnackBar", {
          show: true,
          text: "Le nom du template est obligatoire",
          color: "error",
        });
        return;
      }

      saving.value = true;
      try {
        const payload = {
          name,
          description: templateMetaForm.value.description?.trim() || null,
          is_shared: templateMetaForm.value.is_shared,
          is_active: templateMetaForm.value.is_active,
          logging_enabled: templateMetaForm.value.logging_enabled,
        };
        const res = await llprService.updateTemplate(templateMetaForm.value.id, payload);
        syncTemplateState(res.data);
        templateMetaDialog.value = false;
        store.dispatch("app/setSnackBar", {
          show: true,
          text: "Nom et description mis à jour",
          color: "success",
        });
      } catch (error) {
        store.dispatch("app/setSnackBar", {
          show: true,
          text: "Erreur lors de la mise à jour du template",
          color: "error",
        });
      } finally {
        saving.value = false;
      }
    };

    const openNewBinding = () => {
      isEditingBinding.value = false;
      newBinding.value = {
        context_code: filteredContexts.value[0]?.code || "",
        tool_step: null,
        site_id: null,
        template_id: filteredTemplates.value[0]?.id || null,
        model_id: availableModels.value[0]?.id || "",
        api_key_env_vars: [],
        is_default: true,
        is_active: true,
      };
      bindingConfigJson.value = "{}";
      bindingDialog.value = true;
      loadEnvKeyOptions();
    };

    const openEditBinding = (item) => {
      isEditingBinding.value = true;
      newBinding.value = { ...item, api_key_env_vars: item.api_key_env_vars || [] };
      bindingConfigJson.value = JSON.stringify(item.execution_config || {}, null, 2);
      bindingDialog.value = true;
      loadEnvKeyOptions();
    };

    const saveBinding = async () => {
      if (!newBinding.value.api_key_env_vars || !newBinding.value.api_key_env_vars.length) {
        store.dispatch("app/setSnackBar", { show: true, text: "Sélectionnez au moins une API key", color: "error" });
        return;
      }
      saving.value = true;
      try {
        newBinding.value.execution_config = JSON.parse(bindingConfigJson.value);
        if (isEditingBinding.value) {
          await llprService.updateBinding(newBinding.value.id, newBinding.value);
        } else {
          await llprService.createBinding(newBinding.value);
        }
        bindingDialog.value = false;
        await refreshAll();
      } catch (e) {
        store.dispatch("app/setSnackBar", { show: true, text: "Erreur JSON ou API", color: "error" });
      } finally {
        saving.value = false;
      }
    };

    watch(selectedProviderId, () => {
      if (!bindingDialog.value) return;
      loadEnvKeyOptions();
    });

    const assignTemplate = (template) => {
      activeTab.value = 1;
      const existingBinding = bindings.value.find((b) => b.template_id === template.id);
      if (existingBinding) {
        openEditBinding(existingBinding);
      } else {
        openNewBinding();
        newBinding.value.template_id = template.id;
      }
    };

    const previewActiveRevision = async (template) => {
      if (!template.current_revision_id) return;
      loading.value = true;
      try {
        const res = await llprService.getRevisions(template.id);
        const active = res.data.find(r => r.id === template.current_revision_id);
        if (active) {
          selectedTemplate.value = template;
          previewItem.value = active;
          previewDialog.value = true;
        }
      } finally {
        loading.value = false;
      }
    };

    const activateRevision = async (item) => {
      if (confirm(`Rendre v${item.version_number} ACTIVE ?`)) {
        await llprService.activateRevision(selectedTemplate.value.id, item.id);
        await openRevisionManager(selectedTemplate.value);
        await refreshAll();
      }
    };

    const openDirectEditor = (item) => {
      editorTemplateMeta.value = {
        name: selectedTemplate.value?.name || "",
        description: selectedTemplate.value?.description || "",
      };
      editRevision.value = {
        id: item.id,
        mode: "edit",
        version_number: item.version_number,
        system_prompt: item.system_prompt || "",
        user_prompt: item.user_prompt || "",
        commit_message: item.commit_message || "",
      };
      editorDialog.value = true;
    };

    onMounted(() => {
      refreshAll();
      const devKonami = new Konami([68, 69, 86], () => {
        isDevMode.value = !isDevMode.value;
        store.dispatch("app/setSnackBar", { show: true, text: `DEV MODE ${isDevMode.value ? "ON" : "OFF"}`, color: "orange" });
      });
      konamiManager = new KonamiManager(null, [devKonami]);
    });

    onUnmounted(() => { if (konamiManager) konamiManager.destroy(); });

    return {
      activeTab, loading, filteredTemplates, filteredBindings, searchedTemplates, searchedBindings, filteredContexts,
      templateSearch, bindingSearch, headers, bindingHeaders, revisionHeaders,
      revisionDialog, loadingRevisions, revisions, selectedTemplate, previewDialog, previewItem,
      editorDialog, saving, editRevision, isDevMode, templateDialog, newTemplate, templateMetaDialog, templateMetaForm,
      bindingDialog, isEditingBinding, newBinding, bindingConfigJson, dynamicToolSteps, selectedModel, selectedProviderId, availableModels, selectableModels, allSites, envKeyOptions,
      isSiteRestricted, selectableSites, canWriteBinding,
      icons: { 
        mdiTextBoxMultiple, mdiRefresh, mdiHistory, mdiLink, mdiLinkVariant, mdiPlus, mdiEye, 
        mdiTrashCan, mdiPencil, mdiContentDuplicate, mdiPlayCircle, mdiLinkPlus, 
        mdiCheckCircle, mdiCloseCircle, mdiMagnify, mdiTimelineText, mdiFileDocumentEdit 
      },
      refreshAll, toggleLogging, openRevisionManager, openPromptEditor, saveRevision, openNewTemplate, saveTemplate,
      openTemplateMetaEditor, saveTemplateMeta,
      confirmDeleteTemplate: async (item) => {
        if (confirm(`Supprimer ${item.name} ?`)) {
          await llprService.deleteTemplate(item.id);
          await refreshAll();
        }
      },
      openNewBinding, openEditBinding, duplicateBinding: (item) => {
        isEditingBinding.value = false;
        newBinding.value = { ...item, id: undefined };
        newBinding.value.api_key_env_vars = item.api_key_env_vars || [];
        bindingConfigJson.value = JSON.stringify(item.execution_config || {}, null, 2);
        bindingDialog.value = true;
        loadEnvKeyOptions();
      },
      saveBinding, confirmDeleteBinding: async (item) => {
        if (confirm("Supprimer l'assignation ?")) {
          await llprService.deleteBinding(item.id);
          await refreshAll();
        }
      },
      assignTemplate, isTemplateAssigned: (id) => bindings.value.some(b => b.template_id === id),
      openPreview: (item) => { previewItem.value = item; previewDialog.value = true; },
      activateRevision, openDirectEditor, editorTemplateMeta,
      previewActiveRevision, getSiteName: (id) => allSites.value.find(s => s.site?.id === id)?.site?.name || id,
      getTemplateName: (id) => templates.value.find(t => t.id === id)?.name || id,
      cloneRevision: (item) => openPromptEditor(item),
    };
  },
};
</script>

<style scoped>
.code-block {
  background: #f5f5f5 !important;
  border: 1px solid #e0e0e0 !important;
  border-radius: 4px;
  padding: 12px;
  white-space: pre-wrap !important;
  word-break: break-word;
  font-family: monospace;
  font-size: 0.85rem;
  color: #333 !important;
}
.preserve-whitespace {
  white-space: pre-wrap !important;
}
.text-truncate {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.prompt-search-field {
  max-width: 320px;
}
.prompt-search-field::v-deep .v-input__slot {
  min-height: 32px !important;
}
</style>
